import { useState, useEffect, useRef, useCallback } from "react";
import { Upload, Search, X, Trash2, Tag, Play, Film, Plus, LogIn, LogOut, UserPlus, MessageSquare, ChevronLeft, Shield, User, AlertTriangle } from "lucide-react";

const API_URL = window.location.origin;

// ── API helpers ───────────────────────────────────────────────────────────────
const api = async (method, endpoint, body, token) => {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_URL}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const apiUpload = async (formData, token) => {
  const res = await fetch(`${API_URL}/api/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Upload failed");
  return data;
};

// ── Styles ────────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=Barlow:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --red: #cc0000; --red-b: #ff1a1a; --red-d: #7a0000; --red-bg: rgba(204,0,0,0.08);
    --blk: #080808; --blk2: #111; --blk3: #1a1a1a; --blk4: #222; --blk5: #2a2a2a;
    --wht: #f0f0f0; --grey: #888; --grey-d: #444; --grey-l: #bbb;
  }
  body { background: var(--blk); color: var(--wht); font-family: 'Barlow', sans-serif; min-height: 100vh; overflow-x: hidden; }
  button { font-family: inherit; cursor: pointer; transition: all 0.15s; }
  input, textarea { font-family: inherit; }

  /* NAV */
  .nav { position: fixed; top:0; left:0; right:0; z-index:100; background:var(--blk); border-bottom:2px solid var(--red); display:flex; align-items:stretch; height:64px; }
  .nav-logo { display:flex; align-items:center; padding:0 1.5rem; border-right:1px solid var(--blk4); gap:0.65rem; flex-shrink:0; }
  .nav-hex { width:30px; height:30px; background:var(--red); clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%); display:flex; align-items:center; justify-content:center; }
  .nav-wordmark { font-family:'Barlow Condensed',sans-serif; font-weight:900; font-size:1.25rem; letter-spacing:.08em; text-transform:uppercase; line-height:1; }
  .nav-wordmark span { color:var(--red); }
  .nav-tabs { display:flex; align-items:stretch; }
  .nav-tab { display:flex; align-items:center; gap:.45rem; padding:0 1.4rem; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.85rem; letter-spacing:.12em; text-transform:uppercase; color:var(--grey); border:none; background:none; border-bottom:3px solid transparent; position:relative; top:2px; }
  .nav-tab:hover { color:var(--wht); }
  .nav-tab.active { color:var(--wht); border-bottom-color:var(--red); }
  .nav-badge { background:var(--red); color:#fff; font-size:.65rem; padding:1px 5px; border-radius:2px; font-weight:700; }
  .nav-right { margin-left:auto; display:flex; align-items:center; gap:.5rem; padding:0 1.5rem; }
  .nav-user { display:flex; align-items:center; gap:.5rem; font-size:.85rem; color:var(--grey-l); }
  .nav-user-admin { color:#ffaa00; font-size:.7rem; font-weight:700; letter-spacing:.1em; text-transform:uppercase; background:rgba(255,170,0,.12); padding:2px 7px; border:1px solid rgba(255,170,0,.3); }
  .btn-nav { display:flex; align-items:center; gap:.4rem; background:none; border:1px solid var(--grey-d); color:var(--grey); padding:.4rem .9rem; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.8rem; letter-spacing:.1em; text-transform:uppercase; }
  .btn-nav:hover { border-color:var(--red); color:var(--wht); }
  .btn-nav.primary { background:var(--red); border-color:var(--red); color:#fff; }
  .btn-nav.primary:hover { background:var(--red-b); }

  /* PAGE */
  .page { padding-top:64px; min-height:100vh; }

  /* AUTH MODAL */
  .overlay { position:fixed; inset:0; z-index:200; background:rgba(0,0,0,.88); display:flex; align-items:center; justify-content:center; animation:fade-in .2s ease; }
  .auth-box { width:100%; max-width:400px; background:var(--blk2); border:1px solid var(--blk4); border-top:3px solid var(--red); padding:2.5rem; animation:slide-up .25s ease; }
  .auth-title { font-family:'Barlow Condensed',sans-serif; font-weight:900; font-size:2rem; text-transform:uppercase; letter-spacing:.05em; margin-bottom:.25rem; }
  .auth-sub { font-size:.85rem; color:var(--grey); margin-bottom:2rem; }
  .field { margin-bottom:1.25rem; }
  .field label { display:block; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.75rem; letter-spacing:.15em; text-transform:uppercase; color:var(--grey); margin-bottom:.4rem; }
  .field input { width:100%; background:var(--blk); border:1px solid var(--grey-d); border-left:3px solid var(--red); color:var(--wht); padding:.7rem 1rem; font-size:.95rem; transition:border-color .15s; }
  .field input:focus { outline:none; border-color:var(--red); }
  .field input::placeholder { color:var(--grey-d); }
  .auth-err { background:rgba(204,0,0,.15); border:1px solid var(--red-d); color:#ff9999; padding:.65rem 1rem; font-size:.85rem; margin-bottom:1rem; }
  .btn-full { width:100%; padding:.8rem; background:var(--red); border:none; color:#fff; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:1rem; letter-spacing:.1em; text-transform:uppercase; }
  .btn-full:hover { background:var(--red-b); }
  .btn-full:disabled { opacity:.5; cursor:not-allowed; }
  .auth-switch { text-align:center; margin-top:1.25rem; font-size:.85rem; color:var(--grey); }
  .auth-switch button { background:none; border:none; color:var(--red-b); font-weight:600; padding:0; text-decoration:underline; }

  /* UPLOAD PAGE */
  .upload-layout { display:grid; grid-template-columns:1fr 400px; min-height:calc(100vh - 64px); }
  .upload-main { padding:2.5rem; border-right:1px solid var(--blk4); display:flex; flex-direction:column; gap:2rem; }
  .section-lbl { font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.72rem; letter-spacing:.2em; text-transform:uppercase; color:var(--red); display:flex; align-items:center; gap:.5rem; margin-bottom:.85rem; }
  .section-lbl::after { content:''; flex:1; height:1px; background:var(--blk4); }
  .drop-zone { border:2px dashed var(--grey-d); background:var(--blk2); padding:4rem 2.5rem; text-align:center; cursor:pointer; transition:all .2s; position:relative; overflow:hidden; }
  .drop-zone::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,transparent 40%,rgba(204,0,0,.04) 100%); pointer-events:none; }
  .drop-zone:hover, .drop-zone.drag { border-color:var(--red); background:var(--red-bg); }
  .drop-zone.drag { border-style:solid; }
  .drop-icon { width:64px; height:64px; border:2px solid var(--red-d); display:flex; align-items:center; justify-content:center; margin:0 auto 1.25rem; color:var(--red); }
  .drop-title { font-family:'Barlow Condensed',sans-serif; font-weight:800; font-size:1.75rem; letter-spacing:.04em; text-transform:uppercase; margin-bottom:.4rem; }
  .drop-sub { color:var(--grey); font-size:.875rem; margin-bottom:1.25rem; }
  .btn-choose { display:inline-flex; align-items:center; gap:.4rem; background:var(--red); border:none; color:#fff; padding:.65rem 1.5rem; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.9rem; letter-spacing:.1em; text-transform:uppercase; }
  .btn-choose:hover { background:var(--red-b); }
  .upload-bar { height:3px; background:var(--blk4); margin-top:1.5rem; overflow:hidden; }
  @keyframes prog { 0%{width:5%} 60%{width:75%} 100%{width:95%} }
  .upload-bar-fill { height:100%; background:var(--red); animation:prog 2s ease-in-out infinite; }
  .recent-list { display:flex; flex-direction:column; gap:1px; background:var(--blk4); }
  .recent-row { display:grid; grid-template-columns:108px 1fr auto; align-items:center; gap:1rem; background:var(--blk2); padding:.65rem; cursor:pointer; transition:background .15s; }
  .recent-row:hover { background:var(--blk3); }
  .recent-row.sel { background:var(--red-bg); border-left:3px solid var(--red); }
  .recent-thumb { width:108px; height:60px; object-fit:cover; background:var(--blk4); display:block; flex-shrink:0; }
  .recent-name { font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.95rem; text-transform:uppercase; letter-spacing:.04em; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:.3rem; }
  .tag-chips { display:flex; flex-wrap:wrap; gap:.3rem; }
  .chip { background:var(--blk4); color:var(--grey); font-size:.68rem; font-weight:600; letter-spacing:.08em; text-transform:uppercase; padding:2px 7px; border:1px solid var(--grey-d); }
  .chip.r { background:rgba(204,0,0,.12); color:#ff7777; border-color:var(--red-d); }
  .btn-icon { background:none; border:none; color:var(--grey-d); padding:.4rem; display:flex; align-items:center; }
  .btn-icon:hover { color:var(--red-b); }

  /* TAG PANEL */
  .tag-panel { background:var(--blk2); padding:2rem; display:flex; flex-direction:column; gap:1.4rem; overflow-y:auto; }
  .panel-name { font-family:'Barlow Condensed',sans-serif; font-weight:900; font-size:1.3rem; text-transform:uppercase; letter-spacing:.04em; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .vid-preview { width:100%; aspect-ratio:16/9; background:#000; display:block; border:1px solid var(--blk4); }
  .tag-row { display:flex; gap:.5rem; }
  .tag-input { flex:1; background:var(--blk); border:1px solid var(--grey-d); border-left:3px solid var(--red); color:var(--wht); padding:.6rem .9rem; font-size:.9rem; transition:border-color .15s; }
  .tag-input:focus { outline:none; border-color:var(--red); }
  .tag-input::placeholder { color:var(--grey-d); }
  .btn-add { background:var(--red); border:none; color:#fff; padding:.6rem 1.1rem; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.85rem; letter-spacing:.1em; text-transform:uppercase; }
  .btn-add:hover { background:var(--red-b); }
  .tag-pills { display:flex; flex-wrap:wrap; gap:.45rem; }
  .tag-pill { display:flex; align-items:center; gap:.35rem; background:rgba(204,0,0,.12); border:1px solid var(--red-d); color:var(--wht); padding:.3rem .7rem; font-size:.78rem; font-weight:600; letter-spacing:.07em; text-transform:uppercase; animation:tag-in .2s ease; }
  @keyframes tag-in { from{opacity:0;transform:scale(.85)} to{opacity:1;transform:scale(1)} }
  .pill-x { background:none; border:none; color:var(--grey); padding:0; display:flex; align-items:center; }
  .pill-x:hover { color:var(--red-b); }
  .empty-panel { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; gap:1rem; padding:3rem; }
  .empty-panel p { font-family:'Barlow Condensed',sans-serif; font-size:.95rem; font-weight:600; letter-spacing:.1em; text-transform:uppercase; color:var(--grey-d); }

  /* BROWSE */
  .browse-wrap { padding:2.5rem 3rem; max-width:1600px; margin:0 auto; }
  .browse-hdr { display:flex; align-items:flex-end; justify-content:space-between; margin-bottom:2rem; gap:2rem; flex-wrap:wrap; }
  .browse-ttl { font-family:'Barlow Condensed',sans-serif; font-weight:900; font-size:3.2rem; text-transform:uppercase; letter-spacing:.04em; line-height:1; }
  .browse-ttl span { color:var(--red); }
  .search-bar { display:flex; flex:1; max-width:460px; }
  .search-wrap { position:relative; flex:1; }
  .search-icon { position:absolute; left:.9rem; top:50%; transform:translateY(-50%); color:var(--grey); pointer-events:none; }
  .search-inp { width:100%; background:var(--blk2); border:1px solid var(--grey-d); border-right:none; color:var(--wht); padding:.8rem .9rem .8rem 2.75rem; font-size:.9rem; transition:border-color .15s; }
  .search-inp:focus { outline:none; border-color:var(--red); }
  .search-inp::placeholder { color:var(--grey-d); }
  .btn-search { background:var(--red); border:none; color:#fff; padding:.8rem 1.35rem; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.85rem; letter-spacing:.12em; text-transform:uppercase; }
  .btn-search:hover { background:var(--red-b); }
  .btn-clear-s { background:var(--blk4); border:none; color:var(--grey); padding:.8rem .9rem; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.8rem; letter-spacing:.1em; text-transform:uppercase; }
  .btn-clear-s:hover { background:var(--blk3); color:var(--wht); }
  .filter-bar { display:flex; align-items:center; gap:.5rem; margin-bottom:2rem; flex-wrap:wrap; }
  .filter-lbl { font-family:'Barlow Condensed',sans-serif; font-size:.72rem; font-weight:700; letter-spacing:.15em; text-transform:uppercase; color:var(--grey); margin-right:.5rem; }
  .filter-btn { background:var(--blk3); border:1px solid var(--grey-d); color:var(--grey); padding:.3rem .85rem; font-size:.78rem; font-weight:600; letter-spacing:.08em; text-transform:uppercase; cursor:pointer; transition:all .15s; font-family:'Barlow',sans-serif; }
  .filter-btn:hover, .filter-btn.on { background:var(--red); border-color:var(--red); color:#fff; }
  .vid-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:2px; background:var(--blk4); }
  .vid-card { background:var(--blk2); cursor:pointer; position:relative; overflow:hidden; transition:background .15s; }
  .vid-card:hover { background:var(--blk3); }
  .vid-card:hover .card-overlay { opacity:1; }
  .vid-card:hover .card-del { opacity:1; }
  .thumb-wrap { position:relative; aspect-ratio:16/9; overflow:hidden; background:var(--blk); }
  .card-thumb { width:100%; height:100%; object-fit:cover; display:block; }
  .card-overlay { position:absolute; inset:0; background:rgba(0,0,0,.55); display:flex; align-items:center; justify-content:center; opacity:0; transition:opacity .2s; }
  .play-circle { width:48px; height:48px; background:var(--red); border-radius:50%; display:flex; align-items:center; justify-content:center; }
  .card-body { padding:.9rem; }
  .card-name { font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.95rem; text-transform:uppercase; letter-spacing:.04em; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:.55rem; }
  .card-del { position:absolute; top:.5rem; right:.5rem; background:rgba(0,0,0,.82); border:none; color:var(--grey); padding:.35rem; display:flex; align-items:center; opacity:0; transition:all .15s; }
  .card-del:hover { color:var(--red-b); }
  .empty-grid { grid-column:1/-1; text-align:center; padding:6rem 2rem; }
  .empty-grid h3 { font-family:'Barlow Condensed',sans-serif; font-weight:800; font-size:2rem; text-transform:uppercase; letter-spacing:.05em; color:var(--grey); margin-bottom:.4rem; }
  .empty-grid p { font-size:.9rem; color:var(--grey-d); }

  /* VIDEO DETAIL */
  .detail-wrap { max-width:1100px; margin:0 auto; padding:2.5rem 3rem; }
  .back-btn { display:inline-flex; align-items:center; gap:.5rem; background:none; border:none; color:var(--grey); font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.85rem; letter-spacing:.12em; text-transform:uppercase; margin-bottom:1.5rem; padding:0; }
  .back-btn:hover { color:var(--wht); }
  .detail-title { font-family:'Barlow Condensed',sans-serif; font-weight:900; font-size:2.5rem; text-transform:uppercase; letter-spacing:.04em; margin-bottom:1rem; line-height:1.1; }
  .detail-video { width:100%; aspect-ratio:16/9; background:#000; display:block; border:1px solid var(--blk4); margin-bottom:1.25rem; }
  .detail-meta { display:flex; align-items:center; gap:1.5rem; margin-bottom:1.5rem; color:var(--grey); font-size:.85rem; flex-wrap:wrap; }
  .detail-tags { display:flex; flex-wrap:wrap; gap:.5rem; margin-bottom:2.5rem; padding-bottom:2.5rem; border-bottom:1px solid var(--blk4); }

  /* COMMENTS */
  .comments-section { display:flex; flex-direction:column; gap:1.5rem; }
  .comments-title { font-family:'Barlow Condensed',sans-serif; font-weight:800; font-size:1.6rem; text-transform:uppercase; letter-spacing:.05em; }
  .comment-form { background:var(--blk2); border:1px solid var(--blk4); border-top:2px solid var(--red); padding:1.25rem; }
  .comment-form textarea { width:100%; background:var(--blk); border:1px solid var(--grey-d); color:var(--wht); padding:.75rem 1rem; font-size:.9rem; resize:vertical; min-height:80px; transition:border-color .15s; line-height:1.5; }
  .comment-form textarea:focus { outline:none; border-color:var(--red); }
  .comment-form textarea::placeholder { color:var(--grey-d); }
  .comment-form-footer { display:flex; align-items:center; justify-content:space-between; margin-top:.75rem; }
  .comment-hint { font-size:.8rem; color:var(--grey-d); }
  .btn-post { background:var(--red); border:none; color:#fff; padding:.6rem 1.5rem; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.9rem; letter-spacing:.1em; text-transform:uppercase; }
  .btn-post:hover { background:var(--red-b); }
  .btn-post:disabled { opacity:.5; cursor:not-allowed; }
  .comment-list { display:flex; flex-direction:column; gap:1px; background:var(--blk4); }
  .comment-item { background:var(--blk2); padding:1.1rem 1.25rem; display:flex; gap:1rem; }
  .comment-avatar { width:36px; height:36px; background:var(--red-d); flex-shrink:0; display:flex; align-items:center; justify-content:center; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:1rem; text-transform:uppercase; color:var(--wht); }
  .comment-main { flex:1; min-width:0; }
  .comment-header { display:flex; align-items:center; gap:.6rem; margin-bottom:.4rem; flex-wrap:wrap; }
  .comment-user { font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.9rem; letter-spacing:.05em; text-transform:uppercase; color:var(--wht); }
  .comment-admin-badge { font-size:.65rem; font-weight:700; letter-spacing:.1em; text-transform:uppercase; background:rgba(255,170,0,.12); color:#ffaa00; border:1px solid rgba(255,170,0,.3); padding:1px 6px; }
  .comment-time { font-size:.78rem; color:var(--grey-d); }
  .comment-body { font-size:.9rem; color:var(--grey-l); line-height:1.55; word-break:break-word; }
  .comment-del { background:none; border:none; color:var(--grey-d); padding:.25rem; display:flex; align-items:center; flex-shrink:0; }
  .comment-del:hover { color:var(--red-b); }
  .no-comments { text-align:center; padding:3rem; color:var(--grey-d); }
  .no-comments p { font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:1rem; letter-spacing:.1em; text-transform:uppercase; }
  .login-prompt { background:var(--blk2); border:1px solid var(--blk4); padding:1.5rem; text-align:center; }
  .login-prompt p { color:var(--grey); font-size:.9rem; margin-bottom:.75rem; }
  .login-prompt button { background:var(--red); border:none; color:#fff; padding:.6rem 1.5rem; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:.9rem; letter-spacing:.1em; text-transform:uppercase; }
  .login-prompt button:hover { background:var(--red-b); }

  /* TOAST */
  .toast { position:fixed; bottom:2rem; right:2rem; z-index:500; background:var(--blk3); border:1px solid var(--blk5); border-left:3px solid var(--red); padding:.75rem 1.25rem; font-size:.875rem; color:var(--wht); animation:slide-left .25s ease; max-width:320px; }
  .toast.ok { background:#00cc66; border:1px solid #00ff88; border-left:3px solid #00ff88; color:#000; font-weight:600; }
  @keyframes slide-left { from{transform:translateX(30px);opacity:0} to{transform:translateX(0);opacity:1} }
  @keyframes fade-in { from{opacity:0} to{opacity:1} }
  @keyframes slide-up { from{transform:translateY(24px);opacity:0} to{transform:translateY(0);opacity:1} }
  ::-webkit-scrollbar { width:6px; }
  ::-webkit-scrollbar-track { background:var(--blk); }
  ::-webkit-scrollbar-thumb { background:var(--red-d); }
  ::-webkit-scrollbar-thumb:hover { background:var(--red); }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Toast({ msg, ok, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, []);
  return <div className={`toast ${ok ? "ok" : ""}`}>{msg}</div>;
}

function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="overlay" onClick={onCancel}>
      <div style={{
        background: "var(--blk2)", border: "1px solid var(--blk4)", borderTop: "3px solid var(--red)",
        padding: "2rem", maxWidth: "380px", width: "100%", animation: "slide-up .2s ease"
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginBottom: "1rem" }}>
          <AlertTriangle size={22} color="var(--red)" />
          <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, fontSize: "1.2rem", textTransform: "uppercase", letterSpacing: ".05em" }}>
            Confirm Delete
          </span>
        </div>
        <p style={{ color: "var(--grey-l)", fontSize: ".9rem", marginBottom: "1.5rem", lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: "flex", gap: ".75rem", justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{
            background: "none", border: "1px solid var(--grey-d)", color: "var(--grey)",
            padding: ".55rem 1.25rem", fontFamily: "'Barlow Condensed',sans-serif",
            fontWeight: 700, fontSize: ".85rem", letterSpacing: ".1em", textTransform: "uppercase"
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            background: "var(--red)", border: "none", color: "#fff",
            padding: ".55rem 1.25rem", fontFamily: "'Barlow Condensed',sans-serif",
            fontWeight: 700, fontSize: ".85rem", letterSpacing: ".1em", textTransform: "uppercase"
          }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function AuthModal({ mode, onClose, onAuth }) {
  const [tab, setTab] = useState(mode);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const passwordStrength = () => {
    if (!password) return null;
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    return strength;
  };

  const strength = passwordStrength();
  const strengthColors = ['#cc0000', '#ff6600', '#ffaa00', '#00cc66', '#00ff00'];
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];

  const submit = async () => {
    setErr(""); 
    
    // Client-side validation
    if (tab === "register") {
      if (username.length < 3 || username.length > 30) {
        setErr("Username must be 3-30 characters");
        return;
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
        setErr("Username can only contain letters, numbers, _ and -");
        return;
      }
      if (password.length < 6) {
        setErr("Password must be at least 6 characters");
        return;
      }
      if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
        setErr("Password must contain uppercase, lowercase, and number");
        return;
      }
    }
    
    setLoading(true);
    try {
      const endpoint = tab === "login" ? "/api/auth/login" : "/api/auth/register";
      const data = await api("POST", endpoint, { username, password });
      onAuth(data.token, data.user);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="auth-box" onClick={e => e.stopPropagation()}>
        <div className="auth-title">{tab === "login" ? "Sign In" : "Create Account"}</div>
        <div className="auth-sub">{tab === "login" ? "Welcome back to Rat Trap Fights" : "Join the community"}</div>
        {err && <div className="auth-err">{err}</div>}
        <div className="field">
          <label>Username</label>
          <input value={username} onChange={e => setUsername(e.target.value)}
            placeholder="Enter username" onKeyDown={e => e.key === "Enter" && submit()} />
        </div>
        <div className="field">
          <label>Password</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder="Enter password" onKeyDown={e => e.key === "Enter" && submit()} />
          {tab === "register" && password && (
            <div style={{ marginTop: ".5rem" }}>
              <div style={{ 
                height: "3px", 
                background: "var(--blk4)", 
                borderRadius: "2px",
                overflow: "hidden"
              }}>
                <div style={{ 
                  width: `${(strength / 5) * 100}%`, 
                  height: "100%", 
                  background: strengthColors[strength - 1],
                  transition: "all 0.3s ease"
                }} />
              </div>
              <div style={{ fontSize: ".7rem", color: strengthColors[strength - 1], marginTop: ".25rem" }}>
                {strengthLabels[strength - 1]}
              </div>
            </div>
          )}
        </div>
        <button className="btn-full" onClick={submit} disabled={loading}>
          {loading ? "Please wait..." : tab === "login" ? "Sign In" : "Create Account"}
        </button>
        <div className="auth-switch">
          {tab === "login" ? <>No account? <button onClick={() => { setTab("register"); setErr(""); }}>Register</button></> :
            <>Have an account? <button onClick={() => { setTab("login"); setErr(""); }}>Sign in</button></>}
        </div>
      </div>
    </div>
  );
}

function Comments({ videoId, user, onNeedAuth }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const token = localStorage.getItem("rtf_token");

  const load = useCallback(async () => {
    try {
      const data = await api("GET", `/api/videos/${videoId}/comments`);
      setComments(data);
    } catch {}
  }, [videoId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!body.trim()) return;
    setPosting(true);
    try {
      const c = await api("POST", `/api/videos/${videoId}/comments`, { body }, token);
      setComments(prev => [...prev, c]);
      setBody("");
    } catch (e) { alert(e.message); }
    finally { setPosting(false); }
  };

  const del = async (id) => {
    try {
      await api("DELETE", `/api/comments/${id}`, null, token);
      setComments(prev => prev.filter(c => c.id !== id));
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="comments-section">
      <div className="comments-title">
        <MessageSquare size={20} style={{ verticalAlign: "middle", marginRight: ".5rem", color: "var(--red)" }} />
        Comments ({comments.length})
      </div>

      {user ? (
        <div className="comment-form">
          <textarea
            placeholder="Leave a comment..."
            value={body}
            onChange={e => setBody(e.target.value)}
          />
          <div className="comment-form-footer">
            <span className="comment-hint">Posting as <strong>{user.username}</strong></span>
            <button className="btn-post" onClick={post} disabled={posting || !body.trim()}>
              {posting ? "Posting..." : "Post Comment"}
            </button>
          </div>
        </div>
      ) : (
        <div className="login-prompt">
          <p>Sign in to leave a comment</p>
          <button onClick={onNeedAuth}>Sign In / Register</button>
        </div>
      )}

      {comments.length === 0 ? (
        <div className="no-comments"><p>No comments yet — be the first</p></div>
      ) : (
        <div className="comment-list">
          {comments.map(c => (
            <div key={c.id} className="comment-item">
              <div className="comment-avatar">{c.username[0]}</div>
              <div className="comment-main">
                <div className="comment-header">
                  <span className="comment-user">{c.username}</span>
                  {c.is_admin === 1 && <span className="comment-admin-badge">Admin</span>}
                  <span className="comment-time">{timeAgo(c.created_at)}</span>
                </div>
                <div className="comment-body">{c.body}</div>
              </div>
              {user && (user.is_admin || user.id === c.user_id) && (
                <button className="comment-del btn-icon" onClick={() => del(c.id)}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function RatTrapFights() {
  const [page, setPage] = useState("feed");
  const [videos, setVideos] = useState([]);
  const [pendingVideos, setPendingVideos] = useState([]);
  const [myVideos, setMyVideos] = useState([]);
  const [myPendingVideos, setMyPendingVideos] = useState([]);
  const [feedVideos, setFeedVideos] = useState([]);
  const [feedOffset, setFeedOffset] = useState(0);
  const [hasMoreFeed, setHasMoreFeed] = useState(true);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [detailVideo, setDetailVideo] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [titleInputs, setTitleInputs] = useState({});
  const [tagInputs, setTagInputs] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("rtf_token") || null);
  const [authModal, setAuthModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [uploadTab, setUploadTab] = useState("upload");
  const [editingPending, setEditingPending] = useState(null);
  const fileInputRef = useRef(null);

  const recommendedTags = ["school", "street fight", "knockout", "funny", "mma", "boxing", "wrestling", "submission", "brawl", "one punch"];

  // Restore session
  useEffect(() => {
    if (token) {
      api("GET", "/api/auth/me", null, token)
        .then(u => setUser(u))
        .catch(() => { setToken(null); localStorage.removeItem("rtf_token"); });
    }
  }, []);

  useEffect(() => { fetchVideos(); loadFeed(true); }, []);

  useEffect(() => {
    if (user?.is_admin) {
      fetchPending();
    }
    if (user) {
      fetchMyVideos();
      fetchMyPending();
    }
  }, [user]);

  const showToast = (msg, ok = false) => setToast({ msg, ok });

  const fetchVideos = async () => {
    try {
      const data = await api("GET", "/api/videos");
      setVideos(data);
    } catch {}
  };

  const fetchPending = async () => {
    if (!user?.is_admin) return;
    try {
      const data = await api("GET", "/api/admin/pending", null, token);
      setPendingVideos(data);
    } catch {}
  };

  const fetchMyVideos = async () => {
    if (!user) return;
    try {
      const data = await api("GET", "/api/my-videos", null, token);
      setMyVideos(data);
    } catch {}
  };

  const fetchMyPending = async () => {
    if (!user) return;
    try {
      const data = await api("GET", "/api/my-pending", null, token);
      setMyPendingVideos(data);
    } catch {}
  };

  const loadFeed = async (reset = false) => {
    if (loadingFeed) return;
    setLoadingFeed(true);
    try {
      const offset = reset ? 0 : feedOffset;
      const data = await api("GET", `/api/feed?offset=${offset}&limit=10`);
      
      if (reset) {
        setFeedVideos(data);
        setFeedOffset(10);
      } else {
        setFeedVideos(prev => [...prev, ...data]);
        setFeedOffset(prev => prev + 10);
      }
      
      if (data.length < 10) setHasMoreFeed(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingFeed(false);
    }
  };

  const trackView = async (videoId) => {
    try {
      await api("POST", `/api/videos/${videoId}/view`, {}, token);
    } catch {}
  };

  const handleAuth = (tok, u) => {
    setToken(tok);
    setUser(u);
    localStorage.setItem("rtf_token", tok);
    setAuthModal(null);
    showToast(`Signed in as ${u.username}`, true);
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("rtf_token");
    if (page === "upload") setPage("feed");
    showToast("Signed out");
  };

  const handleFileSelect = (files) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files).filter(f => f.type.startsWith("video/"));
    if (fileArray.length === 0) return;
    
    setStagedFiles(prev => [...prev, ...fileArray]);
    const newInputs = {};
    fileArray.forEach((_, idx) => {
      newInputs[stagedFiles.length + idx] = "";
    });
    setTitleInputs(prev => ({ ...prev, ...newInputs }));
    setTagInputs(prev => ({ ...prev, ...newInputs }));
  };

  const handleUploadFile = async (fileIndex) => {
    const file = stagedFiles[fileIndex];
    if (!file) return;
    
    const title = titleInputs[fileIndex]?.trim();
    if (!title) {
      showToast("Title is required for each video");
      return;
    }
    
    setUploadingFiles(prev => ({ ...prev, [fileIndex]: true }));
    const formData = new FormData();
    formData.append("video", file);
    formData.append("title", title);
    
    const tagsStr = tagInputs[fileIndex] || "";
    const tagsArray = tagsStr.split(",").map(t => t.trim()).filter(Boolean);
    formData.append("tags", JSON.stringify(tagsArray));
    
    try {
      const v = await apiUpload(formData, token);
      
      // Refresh all video lists
      await fetchVideos();
      await fetchMyVideos();
      
      if (user?.is_admin) {
        await fetchPending();
        showToast(`${title} uploaded and approved!`, true);
      } else {
        // Give backend a moment to finish, then fetch pending
        await new Promise(resolve => setTimeout(resolve, 300));
        await fetchMyPending();
        showToast(`Successfully uploaded video, awaiting approval`, true);
        setUploadTab("my-pending");
      }
      
      setSelected({ ...v, tags: tagsArray });
      
      setStagedFiles(prev => prev.filter((_, i) => i !== fileIndex));
      setTitleInputs(prev => {
        const copy = { ...prev };
        delete copy[fileIndex];
        return copy;
      });
      setTagInputs(prev => {
        const copy = { ...prev };
        delete copy[fileIndex];
        return copy;
      });
    } catch (e) { 
      showToast(e.message); 
    } finally {
      setUploadingFiles(prev => {
        const copy = { ...prev };
        delete copy[fileIndex];
        return copy;
      });
    }
  };

  const handleUploadAll = async () => {
    for (let i = 0; i < stagedFiles.length; i++) {
      await handleUploadFile(i);
    }
  };

  const handleRemoveStagedFile = (fileIndex) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== fileIndex));
    setTitleInputs(prev => {
      const copy = { ...prev };
      delete copy[fileIndex];
      return copy;
    });
    setTagInputs(prev => {
      const copy = { ...prev };
      delete copy[fileIndex];
      return copy;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault(); 
    setIsDragging(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleAddTags = async () => {
    if (!selected || !tagInput.trim()) return;
    const newTags = tagInput.split(",").map(t => t.trim()).filter(Boolean);
    const allTags = [...new Set([...selected.tags, ...newTags])];
    try {
      await api("POST", `/api/videos/${selected.id}/tags`, { tags: allTags }, token);
      setTagInput(""); setSelected({ ...selected, tags: allTags });
      await fetchVideos();
    } catch (e) { showToast(e.message); }
  };

  const handleRemoveTag = async (tag) => {
    const updatedTags = selected.tags.filter(t => t !== tag);
    try {
      await api("POST", `/api/videos/${selected.id}/tags`, { tags: updatedTags }, token);
      setSelected({ ...selected, tags: updatedTags });
      await fetchVideos();
    } catch (e) { showToast(e.message); }
  };

  const handleDelete = async (videoId, e) => {
    e?.stopPropagation();
    setConfirmDialog({
      message: "This will permanently delete the video and all its comments. Are you sure?",
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api("DELETE", `/api/videos/${videoId}`, null, token);
          if (selected?.id === videoId) setSelected(null);
          if (detailVideo?.id === videoId) { setDetailVideo(null); setPage("feed"); }
          await fetchVideos();
          await fetchPending();
          await fetchMyVideos();
          await fetchMyPending();
          showToast("Video deleted");
        } catch (err) { showToast(err.message); }
      }
    });
  };

  const handleApprove = async (videoId) => {
    try {
      await api("POST", `/api/admin/approve/${videoId}`, {}, token);
      await fetchVideos();
      await fetchPending();
      showToast("Video approved!", true);
    } catch (e) {
      showToast(e.message);
    }
  };

  const handleReject = async (videoId) => {
    setConfirmDialog({
      message: "This will permanently delete this pending video. Are you sure?",
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api("POST", `/api/admin/reject/${videoId}`, {}, token);
          await fetchPending();
          showToast("Video rejected and deleted");
        } catch (e) {
          showToast(e.message);
        }
      }
    });
  };

  const handleEditPending = (video) => {
    setEditingPending({
      videoId: video.id,
      title: video.title,
      tags: video.tags.join(", ")
    });
  };

  const handleSavePendingEdit = async () => {
    if (!editingPending) return;
    
    try {
      await api("PUT", `/api/videos/${editingPending.videoId}/title`, 
        { title: editingPending.title }, token);
      
      const tagsArray = editingPending.tags.split(",").map(t => t.trim()).filter(Boolean);
      await api("PUT", `/api/videos/${editingPending.videoId}/tags-edit`, 
        { tags: tagsArray }, token);
      
      await fetchMyPending();
      setEditingPending(null);
      showToast("Changes saved!", true);
    } catch (e) {
      showToast(e.message);
    }
  };

  const handleCancelPendingEdit = () => {
    setEditingPending(null);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) { fetchVideos(); return; }
    try {
      const data = await api("GET", `/api/search?q=${encodeURIComponent(searchTerm)}`);
      setVideos(data);
    } catch {}
  };

  const openDetail = (video) => {
    setDetailVideo(video);
    setPage("detail");
    trackView(video.id);
  };

  const allTags = [...new Set(videos.flatMap(v => v.tags))].slice(0, 12);
  const displayedVideos = activeFilter ? videos.filter(v => v.tags.includes(activeFilter)) : videos;

  return (
    <>
      <style>{STYLES}</style>

      {/* ── NAV ── */}
      <nav className="nav">
        <div className="nav-logo">
          <div className="nav-hex"><Film size={13} color="white" /></div>
          <div className="nav-wordmark">Rat Trap <span>Fights</span></div>
        </div>
        <div className="nav-tabs">
          <button className={`nav-tab ${page === "feed" ? "active" : ""}`}
            onClick={() => setPage("feed")}>
            <Play size={14} /> Feed
          </button>
          <button className={`nav-tab ${page === "browse" ? "active" : ""}`}
            onClick={() => setPage("browse")}>
            <Search size={14} /> Browse
            {videos.length > 0 && <span className="nav-badge">{videos.length}</span>}
          </button>
          {user && (
            <button className={`nav-tab ${page === "upload" ? "active" : ""}`}
              onClick={() => setPage("upload")}>
              <Upload size={14} /> Upload
            </button>
          )}
          {user?.is_admin && (
            <button className={`nav-tab ${page === "pending" ? "active" : ""}`}
              onClick={() => setPage("pending")}>
              <AlertTriangle size={14} /> Pending
              {pendingVideos.length > 0 && <span className="nav-badge">{pendingVideos.length}</span>}
            </button>
          )}
        </div>
        <div className="nav-right">
          {user ? (
            <>
              <div className="nav-user">
                <User size={14} />
                <span>{user.username}</span>
                {user.is_admin === 1 && <span className="nav-user-admin"><Shield size={10} style={{verticalAlign:"middle",marginRight:2}} />Admin</span>}
              </div>
              <button className="btn-nav" onClick={handleLogout}><LogOut size={13} /> Sign Out</button>
            </>
          ) : (
            <>
              <button className="btn-nav" onClick={() => setAuthModal("login")}><LogIn size={13} /> Sign In</button>
              <button className="btn-nav primary" onClick={() => setAuthModal("register")}><UserPlus size={13} /> Register</button>
            </>
          )}
        </div>
      </nav>

      {/* ── FEED (Infinite Scroll) ── */}
      {page === "feed" && (
        <div className="page" style={{ background: "var(--blk)" }}>
          <div style={{ maxWidth: "600px", margin: "0 auto", padding: "0" }}>
            {feedVideos.map((v, idx) => (
              <div key={v.id} style={{
                height: "100vh",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderBottom: "1px solid var(--blk4)"
              }}>
                <video
                  controls
                  autoPlay
                  loop
                  muted
                  style={{
                    width: "100%",
                    maxHeight: "100vh",
                    objectFit: "contain",
                    background: "#000"
                  }}
                  src={`${API_URL}/${v.filepath}`}
                  onPlay={() => trackView(v.id)}
                />
                
                <div style={{
                  position: "absolute",
                  bottom: "4rem",
                  left: "1rem",
                  right: "1rem",
                  color: "#fff",
                  textShadow: "0 2px 8px rgba(0,0,0,0.8)",
                  pointerEvents: "none"
                }}>
                  <div style={{
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontWeight: 800,
                    fontSize: "1.5rem",
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                    marginBottom: ".5rem",
                    lineHeight: 1.2
                  }}>
                    {v.title}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem", fontSize: ".85rem" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: ".3rem" }}>
                      <Play size={14} fill="white" /> {v.views || 0} views
                    </span>
                    {v.uploaded_by_username && <span>by {v.uploaded_by_username}</span>}
                  </div>
                  {v.tags && v.tags.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", marginTop: ".5rem" }}>
                      {v.tags.slice(0, 3).map((t, i) => (
                        <span key={i} style={{
                          background: "rgba(204,0,0,0.8)",
                          padding: "2px 8px",
                          fontSize: ".7rem",
                          fontWeight: 700,
                          textTransform: "uppercase"
                        }}>
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => openDetail(v)}
                  style={{
                    position: "absolute",
                    top: "1rem",
                    right: "1rem",
                    background: "rgba(0,0,0,0.7)",
                    border: "1px solid var(--red)",
                    color: "#fff",
                    padding: ".5rem 1rem",
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontWeight: 700,
                    fontSize: ".85rem",
                    letterSpacing: ".1em",
                    textTransform: "uppercase",
                    cursor: "pointer"
                  }}
                >
                  <MessageSquare size={13} style={{ verticalAlign: "middle", marginRight: ".3rem" }} />
                  Comments
                </button>

                {idx === feedVideos.length - 3 && hasMoreFeed && !loadingFeed && (
                  <div ref={(el) => {
                    if (el) {
                      const observer = new IntersectionObserver(([entry]) => {
                        if (entry.isIntersecting) loadFeed();
                      }, { threshold: 0.5 });
                      observer.observe(el);
                      return () => observer.disconnect();
                    }
                  }} style={{ position: "absolute", bottom: 0, height: "1px", width: "100%" }} />
                )}
              </div>
            ))}

            {loadingFeed && (
              <div style={{ padding: "2rem", textAlign: "center", color: "var(--grey)" }}>
                <div className="upload-bar-fill" style={{ 
                  width: "40px", height: "40px", margin: "0 auto", borderRadius: "50%" 
                }} />
                <div style={{ marginTop: "1rem" }}>Loading more...</div>
              </div>
            )}

            {!hasMoreFeed && feedVideos.length > 0 && (
              <div style={{ padding: "3rem", textAlign: "center", color: "var(--grey-d)" }}>
                <div style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em" }}>
                  That's all for now
                </div>
                <div style={{ fontSize: ".85rem", marginTop: ".5rem" }}>
                  Check back later for more fights
                </div>
              </div>
            )}

            {feedVideos.length === 0 && !loadingFeed && (
              <div style={{ padding: "4rem 2rem", textAlign: "center" }}>
                <Film size={64} style={{ opacity: .1, margin: "0 auto 1.5rem", display: "block" }} />
                <div style={{ fontSize: "1.5rem", fontWeight: 800, textTransform: "uppercase", color: "var(--grey)" }}>
                  No videos yet
                </div>
                <div style={{ fontSize: ".9rem", color: "var(--grey-d)", marginTop: ".5rem" }}>
                  Check back soon for fight footage
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── BROWSE ── */}
      {page === "browse" && (
        <div className="page">
          <div className="browse-wrap">
            <div className="browse-hdr">
              <div className="browse-ttl">Fight <span>Library</span></div>
              <div className="search-bar">
                <div className="search-wrap">
                  <Search size={17} className="search-icon" />
                  <input className="search-inp" type="text" placeholder="Search titles and tags..."
                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSearch()} />
                </div>
                <button className="btn-search" onClick={handleSearch}>Search</button>
                {searchTerm && <button className="btn-clear-s" onClick={() => { setSearchTerm(""); fetchVideos(); }}>Clear</button>}
              </div>
            </div>

            {allTags.length > 0 && (
              <div className="filter-bar">
                <span className="filter-lbl">Filter:</span>
                <button className={`filter-btn ${!activeFilter ? "on" : ""}`} onClick={() => setActiveFilter(null)}>All</button>
                {allTags.map((t, i) => (
                  <button key={i} className={`filter-btn ${activeFilter === t ? "on" : ""}`}
                    onClick={() => setActiveFilter(activeFilter === t ? null : t)}>{t}</button>
                ))}
              </div>
            )}

            <div className="vid-grid">
              {displayedVideos.length === 0 ? (
                <div className="empty-grid">
                  <Film size={72} style={{ opacity: .08, display: "block", margin: "0 auto 1.5rem" }} />
                  <h3>{searchTerm || activeFilter ? "No results" : "No videos yet"}</h3>
                  <p>{searchTerm || activeFilter ? "Try a different tag" : "Check back soon for fight footage"}</p>
                </div>
              ) : displayedVideos.map(v => (
                <div key={v.id} className="vid-card" onClick={() => openDetail(v)}>
                  <div className="thumb-wrap">
                    <video className="card-thumb" src={`${API_URL}/${v.filepath}`} muted />
                    <div className="card-overlay"><div className="play-circle"><Play size={20} fill="white" color="white" /></div></div>
                  </div>
                  <div className="card-body">
                    <div className="card-name">{v.title || v.original_name}</div>
                    <div className="tag-chips">
                      {v.tags.length === 0
                        ? <span style={{ fontSize: ".72rem", color: "var(--grey-d)" }}>No tags</span>
                        : v.tags.slice(0, 4).map((t, i) => <span key={i} className="chip r">{t}</span>)
                      }
                      {v.tags.length > 4 && <span className="chip">+{v.tags.length - 4}</span>}
                    </div>
                  </div>
                  {user?.is_admin && (
                    <button className="card-del" onClick={e => handleDelete(v.id, e)}><Trash2 size={14} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── VIDEO DETAIL ── */}
      {page === "detail" && detailVideo && (
        <div className="page">
          <div className="detail-wrap">
            <button className="back-btn" onClick={() => setPage("browse")}>
              <ChevronLeft size={16} /> Back to Library
            </button>
            <div className="detail-title">{detailVideo.title || detailVideo.original_name}</div>
            <video className="detail-video" controls src={`${API_URL}/${detailVideo.filepath}`} />
            <div className="detail-meta">
              <span style={{ color: "var(--grey-d)" }}>Uploaded {timeAgo(detailVideo.upload_date)}</span>
              {detailVideo.uploaded_by_username && (
                <span>by <strong style={{ color: "var(--grey-l)" }}>{detailVideo.uploaded_by_username}</strong></span>
              )}
              {user?.is_admin && (
                <button className="btn-nav" style={{ marginLeft: "auto" }}
                  onClick={e => handleDelete(detailVideo.id, e)}>
                  <Trash2 size={13} /> Delete Video
                </button>
              )}
            </div>
            <div className="detail-tags">
              {detailVideo.tags?.length > 0
                ? detailVideo.tags.map((t, i) => <span key={i} className="chip r">{t}</span>)
                : <span style={{ fontSize: ".85rem", color: "var(--grey-d)" }}>No tags</span>
              }
            </div>
            <Comments
              videoId={detailVideo.id}
              user={user}
              onNeedAuth={() => setAuthModal("login")}
            />
          </div>
        </div>
      )}

      {/* ── UPLOAD ── */}
      {page === "upload" && user && (
        <div className="page">
          <div style={{
            background: "var(--blk2)",
            borderBottom: "1px solid var(--blk4)",
            display: "flex",
            gap: ".5rem",
            padding: "0 2rem"
          }}>
            <button
              onClick={() => setUploadTab("upload")}
              style={{
                background: uploadTab === "upload" ? "var(--blk3)" : "none",
                border: "none",
                borderBottom: uploadTab === "upload" ? "3px solid var(--red)" : "3px solid transparent",
                color: uploadTab === "upload" ? "var(--wht)" : "var(--grey)",
                padding: ".75rem 1.5rem",
                fontFamily: "'Barlow Condensed',sans-serif",
                fontWeight: 700,
                fontSize: ".85rem",
                letterSpacing: ".1em",
                textTransform: "uppercase",
                cursor: "pointer",
                position: "relative",
                top: "1px"
              }}
            >
              <Upload size={13} style={{ verticalAlign: "middle", marginRight: ".4rem" }} />
              Upload New
            </button>
            <button
              onClick={() => setUploadTab("my-pending")}
              style={{
                background: uploadTab === "my-pending" ? "var(--blk3)" : "none",
                border: "none",
                borderBottom: uploadTab === "my-pending" ? "3px solid var(--red)" : "3px solid transparent",
                color: uploadTab === "my-pending" ? "var(--wht)" : "var(--grey)",
                padding: ".75rem 1.5rem",
                fontFamily: "'Barlow Condensed',sans-serif",
                fontWeight: 700,
                fontSize: ".85rem",
                letterSpacing: ".1em",
                textTransform: "uppercase",
                cursor: "pointer",
                position: "relative",
                top: "1px"
              }}
            >
              <AlertTriangle size={13} style={{ verticalAlign: "middle", marginRight: ".4rem" }} />
              Pending Review
              {myPendingVideos.length > 0 && (
                <span style={{
                  background: "#ffaa00",
                  color: "#000",
                  fontSize: ".65rem",
                  padding: "2px 6px",
                  borderRadius: "2px",
                  fontWeight: 700,
                  marginLeft: ".4rem"
                }}>
                  {myPendingVideos.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setUploadTab("my-videos")}
              style={{
                background: uploadTab === "my-videos" ? "var(--blk3)" : "none",
                border: "none",
                borderBottom: uploadTab === "my-videos" ? "3px solid var(--red)" : "3px solid transparent",
                color: uploadTab === "my-videos" ? "var(--wht)" : "var(--grey)",
                padding: ".75rem 1.5rem",
                fontFamily: "'Barlow Condensed',sans-serif",
                fontWeight: 700,
                fontSize: ".85rem",
                letterSpacing: ".1em",
                textTransform: "uppercase",
                cursor: "pointer",
                position: "relative",
                top: "1px"
              }}
            >
              <Film size={13} style={{ verticalAlign: "middle", marginRight: ".4rem" }} />
              My Videos
              {myVideos.length > 0 && <span className="nav-badge" style={{ marginLeft: ".4rem" }}>{myVideos.length}</span>}
            </button>
          </div>

          {uploadTab === "upload" && (
            <div className="upload-layout">
              <div className="upload-main">
                <div>
                  <div className="section-lbl"><Upload size={11} /> New Upload</div>
                  <div className={`drop-zone ${isDragging ? "drag" : ""}`}
                    onDrop={handleDrop}
                    onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onClick={() => fileInputRef.current?.click()}>
                    <input ref={fileInputRef} type="file" accept="video/*" multiple style={{ display: "none" }}
                      onChange={e => handleFileSelect(e.target.files)} />
                    <div className="drop-icon">
                      {stagedFiles.length > 0 ? <Film size={28} /> : <Upload size={28} />}
                    </div>
                    <div className="drop-title">
                      {isDragging ? "Drop Videos Here" : stagedFiles.length > 0 
                        ? `${stagedFiles.length} Video${stagedFiles.length > 1 ? 's' : ''} Ready` 
                        : "Drop Fight Footage Here"}
                    </div>
                    <div className="drop-sub">
                      {stagedFiles.length > 0
                        ? `${(stagedFiles.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024)).toFixed(1)} MB total · Click to add more`
                        : "Drag & drop multiple videos · MP4, MOV, AVI, WebM, MKV · Max 500MB each"}
                    </div>
                    <button className="btn-choose" onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                      <Plus size={15} /> {stagedFiles.length > 0 ? 'Add More' : 'Choose Files'}
                    </button>
                  </div>

                  {stagedFiles.length > 0 && (
                    <div style={{ marginTop: "1.5rem" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                        <div className="section-lbl"><Upload size={11} /> Upload Queue ({stagedFiles.length})</div>
                        <div style={{ display: "flex", gap: ".5rem" }}>
                          <button className="btn-add" onClick={handleUploadAll} 
                            disabled={Object.keys(uploadingFiles).length > 0}>
                            <Upload size={13} style={{ marginRight: ".3rem", verticalAlign: "middle" }} />
                            Upload All
                          </button>
                          <button onClick={() => { setStagedFiles([]); setTitleInputs({}); setTagInputs({}); }} style={{
                            background: "none", border: "1px solid var(--grey-d)", color: "var(--grey)",
                            padding: ".5rem 1rem", fontFamily: "'Barlow Condensed',sans-serif",
                            fontWeight: 700, fontSize: ".8rem", letterSpacing: ".1em", textTransform: "uppercase"
                          }}>
                            Clear All
                          </button>
                        </div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: "1px", background: "var(--blk4)" }}>
                        {stagedFiles.map((file, idx) => (
                          <div key={idx} style={{
                            background: "var(--blk2)", padding: "1rem", 
                            display: "flex", flexDirection: "column", gap: ".75rem"
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                              <div style={{ 
                                width: "40px", height: "40px", background: "var(--red-d)", 
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0
                              }}>
                                <Film size={20} color="var(--red)" />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                                  fontSize: ".95rem", textTransform: "uppercase", letterSpacing: ".04em",
                                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                                }}>
                                  {file.name}
                                </div>
                                <div style={{ fontSize: ".75rem", color: "var(--grey)" }}>
                                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                                </div>
                              </div>
                              {!uploadingFiles[idx] && (
                                <button className="btn-icon" onClick={() => handleRemoveStagedFile(idx)}>
                                  <X size={16} />
                                </button>
                              )}
                            </div>

                            <div>
                              <input
                                className="tag-input"
                                style={{ width: "100%", fontSize: ".85rem", padding: ".5rem .8rem" }}
                                type="text"
                                placeholder="Title (required) *"
                                value={titleInputs[idx] || ""}
                                onChange={e => setTitleInputs(prev => ({ ...prev, [idx]: e.target.value }))}
                                onKeyDown={e => e.key === "Enter" && handleUploadFile(idx)}
                                disabled={uploadingFiles[idx]}
                                required
                              />
                            </div>

                            <div>
                              <input
                                className="tag-input"
                                style={{ width: "100%", fontSize: ".85rem", padding: ".5rem .8rem" }}
                                type="text"
                                placeholder="Tags (optional) - comma separated"
                                value={tagInputs[idx] || ""}
                                onChange={e => setTagInputs(prev => ({ ...prev, [idx]: e.target.value }))}
                                onKeyDown={e => e.key === "Enter" && handleUploadFile(idx)}
                                disabled={uploadingFiles[idx]}
                              />
                              <div style={{ fontSize: ".72rem", color: "var(--grey-d)", marginTop: ".35rem", marginBottom: ".5rem" }}>
                                Click recommended tags or type your own
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem" }}>
                                {recommendedTags.map((tag, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={() => {
                                      const currentTags = tagInputs[idx] || "";
                                      const tagsArray = currentTags.split(",").map(t => t.trim()).filter(Boolean);
                                      if (!tagsArray.includes(tag)) {
                                        const newTags = [...tagsArray, tag].join(", ");
                                        setTagInputs(prev => ({ ...prev, [idx]: newTags }));
                                      }
                                    }}
                                    disabled={uploadingFiles[idx]}
                                    style={{
                                      background: (tagInputs[idx] || "").split(",").map(t => t.trim()).includes(tag) 
                                        ? "var(--red)" 
                                        : "var(--blk3)",
                                      border: "1px solid var(--blk4)",
                                      color: (tagInputs[idx] || "").split(",").map(t => t.trim()).includes(tag)
                                        ? "#fff"
                                        : "var(--grey)",
                                      padding: ".35rem .7rem",
                                      fontSize: ".72rem",
                                      fontFamily: "'Barlow Condensed',sans-serif",
                                      fontWeight: 600,
                                      textTransform: "uppercase",
                                      cursor: "pointer"
                                    }}
                                  >
                                    {tag}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div style={{ display: "flex", gap: ".5rem" }}>
                              <button 
                                className="btn-add" 
                                style={{ flex: 1, padding: ".6rem", fontSize: ".85rem" }}
                                onClick={() => handleUploadFile(idx)}
                                disabled={uploadingFiles[idx]}
                              >
                                {uploadingFiles[idx] ? (
                                  <>
                                    <div className="upload-bar-fill" style={{ 
                                      width: "20px", height: "20px", display: "inline-block",
                                      marginRight: ".4rem", verticalAlign: "middle" 
                                    }} />
                                    Uploading...
                                  </>
                                ) : (
                                  <>
                                    <Upload size={13} style={{ marginRight: ".3rem", verticalAlign: "middle" }} />
                                    Upload Now
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {videos.length > 0 && (
                  <div>
                    <div className="section-lbl"><Film size={11} /> Recent Uploads</div>
                    <div className="recent-list">
                      {videos.slice(0, 8).map(v => (
                        <div key={v.id} className={`recent-row ${selected?.id === v.id ? "sel" : ""}`}
                          onClick={() => setSelected(v)}>
                          <video className="recent-thumb" src={`${API_URL}/${v.filepath}`} muted />
                          <div>
                            <div className="recent-name">{v.title || v.original_name}</div>
                            <div className="tag-chips">
                              {v.tags.length === 0
                                ? <span style={{ fontSize: ".72rem", color: "var(--grey-d)" }}>No tags yet</span>
                                : v.tags.slice(0, 4).map((t, i) => <span key={i} className="chip r">{t}</span>)
                              }
                              {v.tags.length > 4 && <span className="chip">+{v.tags.length - 4}</span>}
                            </div>
                          </div>
                          <button className="btn-icon" onClick={e => handleDelete(v.id, e)}><Trash2 size={15} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="tag-panel">
                {selected ? (
                  <>
                    <div>
                      <div className="section-lbl"><Tag size={11} /> Tagging</div>
                      <div className="panel-name">{selected.title || selected.original_name}</div>
                    </div>
                    <video className="vid-preview" controls src={`${API_URL}/${selected.filepath}`} />
                    <div>
                      <div className="section-lbl"><Tag size={11} /> Add Tags</div>
                      <div className="tag-row">
                        <input className="tag-input" type="text"
                          placeholder="fighter, knockout, round1..."
                          value={tagInput} onChange={e => setTagInput(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && handleAddTags()} />
                        <button className="btn-add" onClick={handleAddTags}>Add</button>
                      </div>
                      <div style={{ fontSize: ".72rem", color: "var(--grey-d)", marginTop: ".4rem" }}>
                        Comma-separate multiple tags · Enter to add
                      </div>
                    </div>
                    {selected.tags && selected.tags.length > 0 && (
                      <div>
                        <div className="section-lbl"><Tag size={11} /> Current Tags</div>
                        <div className="tag-pills">
                          {selected.tags.map((t, i) => (
                            <span key={i} className="tag-pill">
                              {t}
                              <button className="pill-x" onClick={() => handleRemoveTag(t)}><X size={11} /></button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-panel">
                    <Tag size={56} style={{ opacity: .1 }} />
                    <p>Upload videos and add tags</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {uploadTab === "my-pending" && (
            <div className="browse-wrap">
              <div style={{ marginBottom: "2rem" }}>
                <div className="browse-ttl">Pending <span>Review</span></div>
                <div style={{ fontSize: ".9rem", color: "var(--grey)", marginTop: ".5rem" }}>
                  Your uploads waiting for admin approval · Click Edit to modify
                </div>
              </div>

              {myPendingVideos.length === 0 ? (
                <div className="empty-grid">
                  <AlertTriangle size={72} style={{ opacity: .08 }} />
                  <h3>No Pending Videos</h3>
                  <p>All your uploads have been reviewed</p>
                </div>
              ) : (
                <div className="vid-grid">
                  {myPendingVideos.map(v => (
                    <div key={v.id} style={{
                      background: "var(--blk2)",
                      border: editingPending?.videoId === v.id ? "2px solid var(--red)" : "2px solid rgba(255,170,0,.3)"
                    }}>
                      <div className="thumb-wrap">
                        <video className="card-thumb" src={`${API_URL}/${v.filepath}`} muted />
                        <div style={{
                          position: "absolute",
                          top: ".5rem",
                          left: ".5rem",
                          background: "rgba(255,170,0,.9)",
                          color: "#000",
                          padding: ".25rem .6rem",
                          fontSize: ".7rem",
                          fontWeight: 700,
                          textTransform: "uppercase"
                        }}>
                          {editingPending?.videoId === v.id ? "Editing" : "Under Review"}
                        </div>
                      </div>
                      
                      <div style={{ padding: ".9rem" }}>
                        {editingPending?.videoId === v.id ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
                            <div>
                              <div style={{ fontSize: ".7rem", color: "var(--grey)", marginBottom: ".3rem" }}>TITLE</div>
                              <input
                                className="tag-input"
                                style={{ width: "100%", fontSize: ".85rem", padding: ".5rem .7rem" }}
                                value={editingPending.title}
                                onChange={e => setEditingPending(prev => ({ ...prev, title: e.target.value }))}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: ".7rem", color: "var(--grey)", marginBottom: ".3rem" }}>TAGS</div>
                              <input
                                className="tag-input"
                                style={{ width: "100%", fontSize: ".85rem", padding: ".5rem .7rem" }}
                                value={editingPending.tags}
                                onChange={e => setEditingPending(prev => ({ ...prev, tags: e.target.value }))}
                              />
                              <div style={{ display: "flex", flexWrap: "wrap", gap: ".3rem", marginTop: ".5rem" }}>
                                {recommendedTags.slice(0, 6).map((tag, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={() => {
                                      const current = editingPending.tags.split(",").map(t => t.trim()).filter(Boolean);
                                      if (!current.includes(tag)) {
                                        setEditingPending(prev => ({
                                          ...prev,
                                          tags: [...current, tag].join(", ")
                                        }));
                                      }
                                    }}
                                    style={{
                                      background: editingPending.tags.split(",").map(t => t.trim()).includes(tag) ? "var(--red)" : "var(--blk3)",
                                      border: "1px solid var(--blk4)",
                                      color: editingPending.tags.split(",").map(t => t.trim()).includes(tag) ? "#fff" : "var(--grey)",
                                      padding: ".25rem .5rem",
                                      fontSize: ".68rem",
                                      fontWeight: 600,
                                      textTransform: "uppercase",
                                      cursor: "pointer"
                                    }}
                                  >
                                    {tag}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: ".5rem" }}>
                              <button onClick={handleSavePendingEdit} className="btn-add" style={{ flex: 1 }}>✓ Save</button>
                              <button onClick={handleCancelPendingEdit} className="btn-nav">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="card-name">{v.title}</div>
                            <div style={{ fontSize: ".78rem", color: "var(--grey)", marginBottom: ".5rem" }}>
                              Submitted {timeAgo(v.upload_date)}
                            </div>
                            {v.tags && v.tags.length > 0 && (
                              <div className="tag-chips" style={{ marginBottom: ".75rem" }}>
                                {v.tags.slice(0, 4).map((t, i) => <span key={i} className="chip r">{t}</span>)}
                              </div>
                            )}
                            <button
                              onClick={() => handleEditPending(v)}
                              style={{
                                width: "100%",
                                background: "var(--blk3)",
                                border: "1px solid var(--blk4)",
                                color: "var(--grey)",
                                padding: ".6rem",
                                fontFamily: "'Barlow Condensed',sans-serif",
                                fontWeight: 700,
                                fontSize: ".8rem",
                                textTransform: "uppercase",
                                cursor: "pointer"
                              }}
                            >
                              <Tag size={12} style={{ verticalAlign: "middle", marginRight: ".3rem" }} /> Edit Title & Tags
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {uploadTab === "my-videos" && (
            <div className="browse-wrap">
              <div style={{ marginBottom: "2rem" }}>
                <div className="browse-ttl">My <span>Videos</span></div>
                <div style={{ fontSize: ".9rem", color: "var(--grey)", marginTop: ".5rem" }}>
                  Your approved videos that are live on the site
                </div>
              </div>

              {myVideos.length === 0 ? (
                <div className="empty-grid">
                  <Film size={72} style={{ opacity: .08 }} />
                  <h3>No Approved Videos Yet</h3>
                  <p>Upload videos and wait for admin approval</p>
                </div>
              ) : (
                <div className="vid-grid">
                  {myVideos.map(v => (
                    <div key={v.id} className="vid-card" onClick={() => openDetail(v)}>
                      <div className="thumb-wrap">
                        <video className="card-thumb" src={`${API_URL}/${v.filepath}`} muted />
                        <div className="card-overlay">
                          <div className="play-circle"><Play size={20} fill="white" color="white" /></div>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="card-name">{v.title}</div>
                        <div style={{ fontSize: ".78rem", color: "var(--grey)", marginBottom: ".5rem" }}>
                          {v.views || 0} views · {timeAgo(v.upload_date)}
                        </div>
                        <div className="tag-chips">
                          {v.tags.length === 0 ? (
                            <span style={{ fontSize: ".72rem", color: "var(--grey-d)" }}>No tags</span>
                          ) : (
                            v.tags.slice(0, 4).map((t, i) => <span key={i} className="chip r">{t}</span>)
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── PENDING APPROVAL (Admin Only) ── */}
      {page === "pending" && user?.is_admin && (
        <div className="page">
          <div className="browse-wrap">
            <div style={{ marginBottom: "2rem" }}>
              <div className="browse-ttl">Pending <span>Approval</span></div>
              <div style={{ fontSize: ".9rem", color: "var(--grey)", marginTop: ".5rem" }}>
                Review and approve user-submitted videos before they appear on the site
              </div>
            </div>

            {pendingVideos.length === 0 ? (
              <div className="empty-grid" style={{ gridColumn: "1", padding: "6rem 2rem" }}>
                <AlertTriangle size={72} style={{ opacity: .08, display: "block", margin: "0 auto 1.5rem" }} />
                <h3>No Pending Videos</h3>
                <p>All submissions have been reviewed</p>
              </div>
            ) : (
              <div className="vid-grid">
                {pendingVideos.map(v => (
                  <div key={v.id} style={{
                    background: "var(--blk2)",
                    position: "relative",
                    overflow: "hidden",
                    border: "2px solid rgba(255,170,0,.3)"
                  }}>
                    <div className="thumb-wrap">
                      <video className="card-thumb" src={`${API_URL}/${v.filepath}`} muted />
                      <div style={{
                        position: "absolute",
                        top: ".5rem",
                        left: ".5rem",
                        background: "rgba(255,170,0,.9)",
                        color: "#000",
                        padding: ".25rem .6rem",
                        fontSize: ".7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: ".1em"
                      }}>
                        Pending
                      </div>
                    </div>
                    
                    <div style={{ padding: ".9rem" }}>
                      <div className="card-name">{v.title}</div>
                      <div style={{ fontSize: ".78rem", color: "var(--grey)", marginBottom: ".5rem" }}>
                        By <strong>{v.uploaded_by_username}</strong> · {timeAgo(v.upload_date)}
                      </div>

                      {v.tags && v.tags.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: ".3rem", marginBottom: ".75rem" }}>
                          {v.tags.map((t, i) => (
                            <span key={i} style={{
                              background: "rgba(204,0,0,.12)",
                              color: "#ff7777",
                              border: "1px solid var(--red-d)",
                              fontSize: ".68rem",
                              fontWeight: 600,
                              letterSpacing: ".08em",
                              textTransform: "uppercase",
                              padding: "2px 7px"
                            }}>
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <div style={{ display: "flex", gap: ".5rem", marginTop: ".75rem" }}>
                        <button
                          onClick={() => handleApprove(v.id)}
                          style={{
                            flex: 1,
                            background: "var(--red)",
                            border: "none",
                            color: "#fff",
                            padding: ".6rem",
                            fontFamily: "'Barlow Condensed',sans-serif",
                            fontWeight: 700,
                            fontSize: ".85rem",
                            letterSpacing: ".1em",
                            textTransform: "uppercase",
                            cursor: "pointer"
                          }}
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => handleReject(v.id)}
                          style={{
                            background: "none",
                            border: "1px solid var(--grey-d)",
                            color: "var(--grey)",
                            padding: ".6rem 1rem",
                            fontFamily: "'Barlow Condensed',sans-serif",
                            fontWeight: 700,
                            fontSize: ".85rem",
                            letterSpacing: ".1em",
                            textTransform: "uppercase",
                            cursor: "pointer"
                          }}
                        >
                          ✕ Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CONFIRM DIALOG ── */}
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {/* ── AUTH MODAL ── */}
      {authModal && (
        <AuthModal mode={authModal} onClose={() => setAuthModal(null)} onAuth={handleAuth} />
      )}

      {/* ── TOAST ── */}
      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={() => setToast(null)} />}
    </>
  );
}
